import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Trophy, Sparkles, ExternalLink, X, Gift, Clock, Frown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GamePage = () => {
  // Le deuxième paramètre est maintenant gameId (UUID) au lieu de userId
  const { gameType, userId: gameId } = useParams();
  const [businessInfo, setBusinessInfo] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [ownerId, setOwnerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(true);
  const [popupTimer, setPopupTimer] = useState(10);
  const [popupLoading, setPopupLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [nextPlayDate, setNextPlayDate] = useState(null);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  
  // Game specific states
  const [rotation, setRotation] = useState(0);
  const [scratchRevealed, setScratchRevealed] = useState(false);
  const [mysteryOpened, setMysteryOpened] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [formValid, setFormValid] = useState(false);

  // Fonction pour gérer le clic sur "Laisser un avis"
  const handleLeaveReview = () => {
    // Utiliser l'URL Google My Business configurée pour l'établissement
    const gmbUrl = businessInfo?.google_my_business_url || 'https://www.google.com/maps';
    window.open(gmbUrl, '_blank');
    
    // Activer le loading du bouton
    setPopupLoading(true);
    
    // Après 10 secondes, fermer le popup
    setTimeout(() => {
      setPopupLoading(false);
      setShowPopup(false);
    }, 10000);
  };

  // Fonction pour scroller vers la section du jeu
  const scrollToGame = () => {
    const gameSection = document.getElementById('game-section');
    if (gameSection) {
      gameSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchGameInfo();
  }, [gameType, gameId]);

  useEffect(() => {
    // Validation: prénom et email requis
    const isValid = formData.first_name && formData.email;
    setFormValid(isValid);
  }, [formData]);

  // Popup timer
  useEffect(() => {
    if (showPopup && popupTimer > 0) {
      const timer = setTimeout(() => setPopupTimer(popupTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (popupTimer === 0) {
      setShowPopup(false);
    }
  }, [showPopup, popupTimer]);

  const fetchGameInfo = async () => {
    try {
      // Utiliser le nouvel endpoint public
      const response = await axios.get(`${API}/game/public/${gameId}`);
      const data = response.data;
      
      setGameData(data.game);
      setOwnerId(data.user_id);
      setBusinessInfo({
        business_name: data.business.business_name,
        business_category: data.business.business_category,
        game_type: data.game.game_type,
        game_settings: data.game.game_settings || {},
        google_my_business_url: data.business.google_my_business_url
      });
    } catch (error) {
      console.error("Erreur lors de la récupération du jeu:", error);
      if (error.response?.status === 404) {
        toast.error('Jeu non trouvé');
      } else if (error.response?.status === 400) {
        toast.error('Jeu non disponible ou inactif');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async () => {
    console.log('🎮 handlePlay appelé');
    console.log('Formulaire valide?', formValid);
    console.log('Déjà en cours?', playing);
    console.log('Données du formulaire:', formData);
    
    if (!formValid) {
      console.log('❌ Bloqué: formValid=' + formValid);
      toast.error('Veuillez remplir tous les champs requis!');
      return;
    }

    console.log('✅ Démarrage du jeu...');
    setPlaying(true);

    try {
      // Determine prize based on game type
      let selectedPrize;
      const gameSettings = businessInfo?.game_settings || {};
      
      if (gameType === 'wheel') {
        // Utiliser les mêmes valeurs par défaut que pour le rendu visuel
        const prizes = gameSettings.prizes || [
          { name: "10% Off", probability: 30, color: "#3b82f6" },
          { name: "20% Off", probability: 20, color: "#10b981" },
          { name: "30% Off", probability: 10, color: "#f59e0b" },
          { name: "Perdu", probability: 40, color: "#ef4444" }
        ];
        
        const totalProbability = prizes.reduce((sum, p) => sum + p.probability, 0);
        const randNum = Math.random() * totalProbability;
        
        let cumulative = 0;
        let selectedPrizeIndex = 0;
        selectedPrize = prizes[0];
        
        for (let i = 0; i < prizes.length; i++) {
          cumulative += prizes[i].probability;
          if (randNum <= cumulative) {
            selectedPrize = prizes[i];
            selectedPrizeIndex = i;
            break;
          }
        }
        
        // === CALCUL DE ROTATION - APPROCHE DIRECTE ===
        // Avec 4 segments, chaque segment fait 90°
        // Le segment 0 (10% Off, BLEU) est en haut-droite, son centre est à -45° (ou 315°)
        // Le segment 1 (20% Off, VERT) est en bas-droite, son centre est à 45°
        // Le segment 2 (30% Off, ORANGE) est en bas-gauche, son centre est à 135°
        // Le segment 3 (Perdu, ROUGE) est en haut-gauche, son centre est à 225°
        //
        // La flèche pointe vers le HAUT (-90° ou 270°)
        // 
        // Pour aligner le segment i sous la flèche (à -90°), on doit:
        // - Prendre le centre du segment: midAngle = 90*i + 45 - 90 = 90*i - 45
        // - Calculer combien il faut tourner pour que midAngle arrive à -90°
        // - rotation = -90 - midAngle = -90 - (90*i - 45) = -45 - 90*i
        //
        // Mais après observation, les textes sont décalés visuellement.
        // Testons avec une rotation directe basée sur l'index.
        
        const segmentAngle = 360 / prizes.length;
        
        // Rotation directe: pour le segment i, on tourne de (n - i) * segmentAngle
        // Cela place le segment i en haut (sous la flèche)
        // On ajoute segmentAngle/2 pour centrer le segment sous la flèche
        let targetAngle = ((prizes.length - selectedPrizeIndex) * segmentAngle) - (segmentAngle / 2);
        
        // Normaliser l'angle entre 0 et 360
        while (targetAngle < 0) targetAngle += 360;
        while (targetAngle >= 360) targetAngle -= 360;
        
        // Ajouter plusieurs tours complets pour l'effet de spin (5-7 tours)
        const spins = 5 + Math.floor(Math.random() * 3);
        const finalRotation = (spins * 360) + targetAngle;
        
        console.log('🎰 DEBUG ROUE:');
        console.log('   - Nombre de segments:', prizes.length);
        console.log('   - Angle par segment:', segmentAngle + '°');
        console.log('   - Prix sélectionné:', selectedPrize.name, '(index:', selectedPrizeIndex + ')');
        console.log('   - Angle cible:', targetAngle + '°');
        console.log('   - Rotation finale:', finalRotation + '°');
        
        // Animer la roue
        await new Promise(resolve => setTimeout(resolve, 100));
        setRotation(finalRotation);
        await new Promise(resolve => setTimeout(resolve, 4000));
        
        // Pause de 1.5s pour voir le résultat sur la roue avant l'écran de félicitations
        await new Promise(resolve => setTimeout(resolve, 1500));
      } else {
        // Promo code
        const promoCode = gameSettings.promo_code || `PROMO${Math.floor(Math.random() * 10000)}`;
        selectedPrize = { name: promoCode, color: "#ec4899" };
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Generate unique redemption code
      const redemptionCode = `${gameId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Save customer via API
      try {
        const playResponse = await axios.post(`${API}/game/play`, {
          game_id: gameId,
          customer: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone || '',
            prize_won: selectedPrize.name,
            consent_marketing: true,
            redemption_code: redemptionCode
          }
        });
        
        if (playResponse.data.already_played) {
          const nextPlay = new Date(playResponse.data.next_play_date);
          setAlreadyPlayed(true);
          setNextPlayDate(nextPlay);
          toast.error(`Vous avez déjà joué! Revenez le ${nextPlay.toLocaleDateString('fr-FR')}`);
          setPlaying(false);
          return;
        }
      } catch (error) {
        console.error('Erreur sauvegarde client:', error);
      }
      
      // Envoyer SMS avec prix et QR code (si pas "Perdu" et a un téléphone)
      console.log('📱 Vérification SMS - Prix:', selectedPrize.name, '| Téléphone:', formData.phone);
      
      if (selectedPrize.name.toLowerCase() !== "perdu" && formData.phone) {
        console.log('✅ Envoi du SMS...');
        try {
          const smsResponse = await axios.post(`${API}/game/send-prize-sms`, {
            user_id: ownerId,
            phone: formData.phone,
            prize: selectedPrize.name,
            business_name: businessInfo?.business_name || "Markeap",
            redemption_code: redemptionCode
          });
          console.log('✅ SMS envoyé avec succès:', smsResponse.data);
        } catch (error) {
          console.error("❌ Erreur lors de l'envoi du SMS de prix:", error);
          console.error("Détails de l'erreur:", error.response?.data);
        }
      } else {
        console.log('⏭️ SMS ignoré - Prix est Perdu ou pas de téléphone');
      }
      
      setResult({
        prize: selectedPrize,
        game_type: gameType,
        message: selectedPrize.name.toLowerCase() === "perdu" 
          ? "Retentez votre chance une prochaine fois !" 
          : `Félicitations! Vous avez gagné: ${selectedPrize.name}! Consultez votre SMS.`
      });
      
      console.log('✅ Jeu terminé - Prix:', selectedPrize.name);
      
      if (selectedPrize.name.toLowerCase() === "perdu") {
        toast.error(`Dommage! Retentez votre chance une prochaine fois !`);
      } else {
        toast.success(`Vous avez gagné: ${selectedPrize.name}! Un SMS vous a été envoyé.`);
      }
    } catch (error) {
      console.error('Erreur lors du jeu:', error);
      toast.error('Erreur lors du jeu');
      setPlaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  // Google My Business Popup
  if (showPopup) {
    return (
      <div className="min-h-screen bg-black/50 flex items-center justify-center p-6 fixed inset-0 z-50">
        <Card className="max-w-md w-full p-8 relative">
          <div className="text-center">
            {/* Icône cadeau */}
            <div className="w-24 h-24 bg-gradient-to-br from-[#2E59D7] to-[#0082FB] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Gift className="w-12 h-12 text-white" />
            </div>
            
            {/* Titre */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Laissez-nous un avis et gagnez le gros lot
            </h2>
            
            {/* Étapes */}
            <div className="text-left space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">1</span>
                <p className="text-gray-700 pt-1">Vous allez être redirigé vers notre page Google</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">2</span>
                <p className="text-gray-700 pt-1">Laissez-nous un avis ⭐️</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">3</span>
                <p className="text-gray-700 pt-1">Revenez sur cette page et profitez de votre cadeau ! 🎁</p>
              </div>
            </div>

            {/* Bouton */}
            <Button
              onClick={handleLeaveReview}
              disabled={popupLoading}
              className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7] py-6 text-lg"
              size="lg"
            >
              {popupLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>On vérifie votre action.. Veuillez patienter</span>
                </div>
              ) : (
                "Laisser un avis"
              )}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // IMPORTANT: Cette définition doit être identique à celle dans handlePlay
  const prizes = businessInfo?.game_settings?.prizes || [
    { name: "10% Off", probability: 30, color: "#3b82f6" },
    { name: "20% Off", probability: 20, color: "#10b981" },
    { name: "30% Off", probability: 10, color: "#f59e0b" },
    { name: "Perdu", probability: 40, color: "#ef4444" }
  ];

  return (
    <div className="min-h-screen bg-[#1e3a5f]">
      {/* Hero Section avec Background Image */}
      <div 
        className="relative h-[280px] flex flex-col items-center justify-center text-center px-4"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("https://images.unsplash.com/photo-1513885535751-8b9238bd345a?w=1200&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
        }}
      >
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-3 drop-shadow-lg">
          ÇA TE DIRAIT UN CADEAU CHEZ<br/>
          <span className="text-yellow-400">{businessInfo?.business_name || 'Notre Restaurant'}</span> ? 🥵
        </h1>
        <h2 className="text-lg sm:text-xl text-white/90 mb-6 font-medium">
          Fais tourner la roue, jeu 100% gagnant
        </h2>
        <button
          onClick={scrollToGame}
          className="bg-white text-gray-900 font-bold py-3 px-8 rounded-full text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Je veux jouer
        </button>
      </div>

      {/* Section du Jeu */}
      <div id="game-section" className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          {!result ? (
            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
              {/* Roue à gauche */}
              <div className="flex-shrink-0">
                {gameType === 'wheel' && (
                  <div className="relative w-[320px] h-[320px] sm:w-[380px] sm:h-[380px]">
                    {/* Flèche indicatrice */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-30">
                      <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[40px] border-t-yellow-400 drop-shadow-lg"></div>
                    </div>
                    
                    {/* Conteneur extérieur avec bordure blanche */}
                    <div className="w-full h-full rounded-full border-[12px] border-white shadow-2xl relative overflow-hidden">
                      {/* Roue tournante */}
                      <div 
                        className="absolute inset-0 rounded-full overflow-hidden"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          transition: playing ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                        }}
                      >
                        {/* SVG pour les segments */}
                        <svg className="w-full h-full" viewBox="0 0 200 200">
                          {prizes.map((prize, i) => {
                            const segmentAngle = 360 / prizes.length;
                            const startAngle = segmentAngle * i - 90;
                            const endAngle = startAngle + segmentAngle;
                            const startRad = (startAngle * Math.PI) / 180;
                            const endRad = (endAngle * Math.PI) / 180;
                            const x1 = 100 + 100 * Math.cos(startRad);
                            const y1 = 100 + 100 * Math.sin(startRad);
                            const x2 = 100 + 100 * Math.cos(endRad);
                            const y2 = 100 + 100 * Math.sin(endRad);
                            const largeArcFlag = segmentAngle > 180 ? 1 : 0;
                            const path = `M 100 100 L ${x1} ${y1} A 100 100 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
                            
                            return (
                              <path
                                key={i}
                                d={path}
                                fill={prize.color}
                                stroke="#1e3a5f"
                                strokeWidth="2"
                              />
                            );
                          })}
                        </svg>
                        
                        {/* Textes des prix */}
                        {prizes.map((prize, i) => {
                          const segmentAngle = 360 / prizes.length;
                          const midAngleDeg = segmentAngle * i + segmentAngle / 2 - 90;
                          const midAngleRad = (midAngleDeg * Math.PI) / 180;
                          const radius = 32;
                          const x = 50 + radius * Math.cos(midAngleRad);
                          const y = 50 + radius * Math.sin(midAngleRad);
                          
                          return (
                            <div 
                              key={`text-${i}`}
                              className="absolute pointer-events-none"
                              style={{
                                left: `${x}%`,
                                top: `${y}%`,
                                transform: `translate(-50%, -50%) rotate(${midAngleDeg + 90}deg)`,
                                zIndex: 10,
                              }}
                            >
                              <span 
                                className="text-white font-bold text-sm whitespace-nowrap block text-center"
                                style={{
                                  textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
                                }}
                              >
                                {prize.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Centre de la roue avec logo */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full border-4 border-[#1e3a5f] shadow-xl flex items-center justify-center z-20">
                        <span className="text-[#1e3a5f] font-bold text-xs text-center leading-tight">
                          {businessInfo?.business_name?.substring(0, 8) || 'LOGO'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Formulaire à droite */}
              <div className="w-full max-w-md">
                {alreadyPlayed ? (
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
                    <Clock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-4 text-white">Déjà joué!</h2>
                    <p className="text-white/80 mb-4">
                      Vous avez déjà participé à ce jeu récemment.
                    </p>
                    <div className="bg-white/20 p-4 rounded-lg mb-4">
                      <p className="text-sm text-white/70 mb-1">Vous pourrez rejouer le :</p>
                      <p className="text-xl font-bold text-yellow-400">
                        {nextPlayDate?.toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          day: 'numeric',
                          month: 'long'
                        })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl p-6 shadow-2xl">
                    <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2 text-center">
                      Fais tourner la roue
                    </h2>
                    <p className="text-gray-500 text-center mb-6 text-sm">
                      Dis nous qui tu es pour qu'on puisse te gâter 👇
                    </p>

                    <div className="space-y-4">
                      <div>
                        <Input
                          placeholder="Ton prénom"
                          value={formData.first_name}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          disabled={result}
                          className="w-full py-3 px-4 rounded-xl border-2 border-gray-200 focus:border-[#1e3a5f] text-gray-800"
                        />
                      </div>

                      <div>
                        <Input
                          placeholder="Ton numéro de téléphone"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          disabled={result}
                          className="w-full py-3 px-4 rounded-xl border-2 border-gray-200 focus:border-[#1e3a5f] text-gray-800"
                        />
                      </div>

                      <div>
                        <Input
                          type="email"
                          placeholder="Ton adresse email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          disabled={result}
                          className="w-full py-3 px-4 rounded-xl border-2 border-gray-200 focus:border-[#1e3a5f] text-gray-800"
                        />
                      </div>

                      {/* Checkbox marketing */}
                      <div className="flex items-start gap-3 py-2">
                        <input
                          type="checkbox"
                          id="marketing"
                          checked={acceptMarketing}
                          onChange={(e) => setAcceptMarketing(e.target.checked)}
                          className="mt-1 w-5 h-5 rounded border-gray-300 text-[#1e3a5f] focus:ring-[#1e3a5f]"
                        />
                        <label htmlFor="marketing" className="text-sm text-gray-600">
                          Je veux recevoir des cadeaux de la part de <span className="font-semibold">{businessInfo?.business_name || 'ce restaurant'}</span>
                        </label>
                      </div>

                      <Button 
                        onClick={handlePlay}
                        disabled={!formValid || playing || result}
                        className={`w-full py-4 text-lg font-bold rounded-xl transition-all ${
                          formValid && !result 
                            ? 'bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white shadow-lg hover:shadow-xl' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {playing ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>La roue tourne...</span>
                          </div>
                        ) : result ? 'Terminé!' : 'Lancer la roue 🎰'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Écran de résultat */
            <div className="max-w-lg mx-auto bg-white rounded-2xl p-8 text-center shadow-2xl">
              {result.prize.name.toLowerCase() === "perdu" ? (
                <div className="w-24 h-24 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Frown className="w-12 h-12 text-white" />
                </div>
              ) : (
                <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-12 h-12 text-white" />
                </div>
              )}
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
                {result.prize.name.toLowerCase() === "perdu" ? "Dommage !" : "Félicitations !"}
              </h2>
              <div className="text-5xl font-extrabold mb-6 text-[#1e3a5f]">
                {result.prize.name.toLowerCase() === "perdu" ? "😢" : result.prize.name}
              </div>
              <p className="text-gray-600 mb-4">{result.message}</p>
              {result.prize.name.toLowerCase() !== "perdu" && (
                <p className="text-sm text-gray-500">
                  📱 Un SMS a été envoyé au {formData.phone}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamePage;
